import {AccessLevel} from '@colonist/types'

export function isValueTrue(value: any): value is true {
    return value === true
}

export function isValueBoolean(value: any): value is boolean {
    return typeof value === 'boolean'
}

export function isValueNumber(value: any): value is number {
    return (typeof value === 'number') && !Number.isNaN(value) && Number.isFinite(value)
}

export function isValueNullish(value: any): value is undefined | null {
    if(value === undefined) return true
    if(value === null) return true
    return false
}

export function isValueNumberArray(value: any): value is number[] {
    return Array.isArray(value) && value.every(isValueNumber)
}

export function isValueNumberBetween(value: any, minValue: number, maxValue: number): value is number {
    return isValueNumber(value) && (value >= minValue) && (value <= maxValue)
}

export function isValueString(value: any): value is string {
    return typeof value === 'string'
}

export function isValueNonEmptyString(value: any): value is string {
    return isValueString(value) && (value != '')
}

export function isValueStringBetween(value: any, minLength: number, maxLength: number): value is string {
    return isValueString(value) && isValueNumberBetween(value.length, minLength, maxLength)
}

export function isValueNumericEnum<T>(value: T, theEnum: any): value is NonNullable<T> {
    if(!isValueNumber(value)) return false
    if(theEnum[value] == undefined) return false
    return true
}

export function isValueNumericEnumArray<T>(value: T, theEnum: any): value is NonNullable<T> {
    return Array.isArray(value) && value.every((v) => isValueNumericEnum(v, theEnum))
}

export function isValueEmail(value: any): value is string {
    // https://ui.dev/validate-email-address-javascript/
    return isValueString(value) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export function isValueRequiredAccessLevel(value: any, requiredAccessLevel: AccessLevel): value is AccessLevel {
    const reqACLAdmin = requiredAccessLevel == AccessLevel.Administrator
    const reqACLManager = requiredAccessLevel == AccessLevel.Manager
    const reqACLModerator = requiredAccessLevel == AccessLevel.Moderator
    const reqACLUser = requiredAccessLevel == AccessLevel.User

    switch(value) {
        case undefined: return false
        case AccessLevel.Administrator: if(reqACLAdmin || reqACLManager || reqACLModerator || reqACLUser) return true; break
        case AccessLevel.Manager: if(reqACLManager || reqACLModerator || reqACLUser) return true; break
        case AccessLevel.Moderator: if(reqACLModerator || reqACLUser) return true; break
        case AccessLevel.User: if(reqACLUser) return true; break
    }
    return false
}

export function isValueDate<T>(value: T): value is NonNullable<T> {
    if(value instanceof Date && !isNaN(value.getTime())) return true
    if(isValueString(value) && !isNaN(Date.parse(value))) return true
    return false
}

export function isValuePlainObject(value: any): value is Record<any, any> {
    if(typeof value !== 'object') return false
    if(Array.isArray(value)) return false

    return true
}

export function isValueArray(value: any): value is any[] {
    return Array.isArray(value)
}

export function isValueFile(value: any): value is File {
    if(value == undefined) return false
    return value instanceof File
}
